import {JWspUriChangeMsg, JWspWorkingInitStates, JWspWorkingState, Wsp, WSP, wspCd, wspRef, wspUri} from "lib/wsp/wsp";
import {JSrcFields, srcUri} from "lib/wsp/src";
import {EditorState, Extension, Text, Transaction} from "res/codemirror/state";
import {EditorView} from "res/codemirror/view";
import {EventsMgr, IEvents, IEventsMgr} from "lib/commons/events";
import {IReg} from "lib/commons/registry";
import {IItemUiEnv} from "back/wsp/views/itemMain";
import {ItemType} from "lib/wsp/wspMetaUi";
import {EWspChangesEvts, EWspWorkingSt, ICollabLock, JWspWorkingMsgStateIn, JWspWorkingMsgStateOut, WspsLive} from "lib/wsp/wspsLive";
import {ITEM} from "lib/wsp/item";
import {EHttpStatusCode, IO, RespError} from "lib/commons/io/io";
import {POPUP} from "back/commons/widgets/popups";

/**
 * Gestionnaire de tous les CmEditor en cours d'un atelier
 * avec synchronisation des éditeurs référençant le même contenu.
 * @see WspsLive.cmEditors
 */
export interface ICmEditors {

	/** Charge ou recharge le contenu abandonnant les modifs en cours. */
	refetchContent(wspUri: wspUri): Promise<void>

	/** Eneregistre toutes les sources dirty. */
	saveAll(addWaiterTo: Promise<void>[]): void

	save(wspItemUri: wspUri, waiters: Promise<any>[]): void

	/** Appelé sur les msg JWspUriChangeMsg par le WspsLive. */
	onWspUriChangeMsg(m: JWspUriChangeMsg,): Promise<void>

	onWspWorkingInitMsg(urisStates: JWspWorkingInitStates): Promise<void>

	onWspWorkingStateMsg(m: JWspWorkingMsgStateIn): void

	isDirty(wspUri: wspUri): boolean

	/** Demande si le WspsLive peut poser un lock externe (comments dynGen) et l'ajoute le cas échénat pour cette source. */
	tryLockExternals(wspRef: wspRef): boolean

	/** Supprime le lock externe */
	unlockExternals(wspRef: wspRef): void
}

/** Enveloppe un EditorView */
export interface ICmEditor {

	wspUri: wspUri

	editorView: EditorView

	contentEvents: IEventsMgr<IContentCmEditorEvents>

	contentState: ICtEdState

	/** Autre device verrouillant actuellement cette source. */
	lockedBy: ICollabLock | undefined

	save(): void

	close(): void
}

export type ICtEdState = 'fetching' | 'fetchFailed' | 'saving' | 'fresh' | 'dirty';
export type ICtEdEvent = ICtEdState | 'saveFailed';

/** Events pour la getrsion commune d'une source inter-CmEditor. */
export interface IContentCmEditorEvents extends IEvents {

	/** Evènements relatifs au changement d'état du contenu. */
	onCtStateChange: (state: ICtEdEvent, e?: any) => void;

	/** Evènements relatifs vérrouillage / déverrouillage de l'éditeur modifiant l'état readOnly des éditeurs. */
	onLock: (locked: boolean) => void;

	// /** Détection d'un changement de permission sur cette source. NON géré par la place de la mainView. */
	// onPermChange: (newFields: JSrcFields) => void;
}

class CmEditors implements ICmEditors {

	/** CM Editeurs synchronisés référencés par son wspUri (de la res source de l'item). */
	editorsByUri = new Map<wspUri, SynchedEditors>();

	/** CM Editeurs synchronisés référencés par le wspUri de son item. */
	editorsByItemUri = new Map<wspUri, SynchedEditors[]>();

	constructor(public wspsLive: WspsLive) {}

	async refetchContent(wspUri: wspUri): Promise<void> {
		return this.editorsByUri.get(wspUri)?.refetchContent();
	}

	async onWspUriChangeMsg(m: JWspUriChangeMsg): Promise<void> {
		//console.log("onWspUriChangeMsg::::::", m);
		if (m.type === EWspChangesEvts.u) {
			const wspUri = WSP.buildWspUri(m.wspCd, m.srcUri);
			//Si item mono-res
			const syncEd = this.editorsByUri.get(wspUri);
			if (syncEd && syncEd.ctState !== "saving") syncEd.refetchContent();
			//Si item multi-res
			const syncEds = this.editorsByItemUri.get(wspUri);
			if (syncEds) {
				const eds = syncEds.filter((ed) => ed.ctState !== "saving");
				if (eds.length > 0) {
					const newSts = await this.wspsLive.wspServer.config.wspSrcUrl.fetchJson<JSrcFields[]>(IO.qs("param", m.wspCd, "refUris", eds.map((s) => s.srcUri).join('\t'), "fields", "srcStamp*srcDt"));
					for (let i = 0; i < newSts.length; i++) {
						const newSt = newSts[i];
						const syncEd = eds[i];
						if (newSt.srcStamp ? syncEd.fields.srcStamp !== newSt.srcStamp : syncEd.fields.srcDt !== newSt.srcDt) syncEd.refetchContent();
					}
				}
			}
		} else if (m.type === EWspChangesEvts.r) {
			//no-op les EditorView seront éliminés ce qui purgera le syncEd.
		}

		// Géré par abonnement du widget à la place de l'item directement.
		// else if (m.type === EWspChangesEvts.perm) {
		// } else if (m.type === EWspChangesEvts.lcSt || m.type === EWspChangesEvts.rspUsrs) {
		// }
	}

	async onWspWorkingInitMsg(urisStates: JWspWorkingInitStates): Promise<void> {
		for (const wspUri in urisStates) {
			const syncEd = this.editorsByUri.get(wspUri);
			if (syncEd) {
				//un editeur existe...
				const uriStates = urisStates[wspUri];
				if (uriStates.reject || uriStates.srcStamp ? uriStates.srcStamp !== syncEd.fields.srcStamp : uriStates.srcDt !== syncEd.fields.srcDt) {
					//édition concurrente ou chgt de droits pendant la rupture de la connection WebSocket
					//XXX dispatch info avant reload ? Si dirty, createItemConflict , copie presse-papier ?
					await syncEd.refetchContent();
				} else {
					syncEd.refreshLockState(uriStates.states);
				}
			}
		}
	}

	onWspWorkingStateMsg(m: JWspWorkingMsgStateIn) {
		const syncEd = this.editorsByUri.get(m.uri);
		if (syncEd) {
			if (m.state === EWspWorkingSt.w) {
				syncEd.lock(m.clId, m.account);
			} else {
				if (syncEd.lockedBy && syncEd.lockedBy.clId === m.clId) {
					syncEd.unlock();
				}
			}
		}
	}

	isDirty(wspUri: wspUri): boolean {
		const syncEd = this.editorsByUri.get(wspUri);
		return syncEd ? syncEd.ctState === "dirty" : false;
	}

	/** Pas de comments actuellement dans les fichiers de type code source. */
	tryLockExternals(wspRef: wspRef): boolean {return true}

	unlockExternals(wspRef: wspRef) {}

	saveAll(addWaiterTo: Promise<void>[]) {
		for (const ed of this.editorsByUri.values()) {
			if (ed.ctState === "dirty") addWaiterTo.push(ed.save(this.wspsLive.findWsp(ed.wspCd, true), null));
		}
	}

	save(wspItemUri: wspUri, waiters: Promise<any>[]) {
		const eds = this.editorsByItemUri.get(wspItemUri);
		if (eds) for (const ed of eds) {
			if (ed.ctState === "dirty") waiters.push(ed.save(this.wspsLive.findWsp(ed.wspCd, true), null));
		}
	}

	getOrCreateSyncEd(wspCd: wspCd, srcUri: srcUri): SynchedEditors {
		const wspUri = WSP.buildWspUri(wspCd, srcUri);
		let syncEd = this.editorsByUri.get(wspUri);
		if (!syncEd) {
			const wspUriItem = ITEM.getSrcUriType(wspUri) === "res" ? ITEM.extractItemUri(wspUri) : null;
			syncEd = new SynchedEditors(this.wspsLive, wspCd, srcUri, wspUri, wspUriItem);
			this.editorsByUri.set(wspUri, syncEd);
			if (wspUriItem) {
				const list = this.editorsByItemUri.get(wspUriItem);
				if (list) {
					list.push(syncEd);
				} else {
					this.editorsByItemUri.set(wspUriItem, [syncEd]);
				}
			}
			syncEd.refetchContent();
		}
		return syncEd;
	}

	removeSyncEd(syncEd: SynchedEditors) {
		this.editorsByUri.delete(syncEd.wspUri);
		const list = this.editorsByItemUri.get(syncEd.wspUriItem);
		if (list) {
			if (list.length === 1) {
				if (list[0] === syncEd) this.editorsByItemUri.delete(syncEd.wspUriItem);
			} else {
				const idx = list.indexOf(syncEd);
				if (idx >= 0) list.splice(idx, 1);
			}
		}
	}
}

/** Regroupement des CmEditor qui pointent la même source. */
class SynchedEditors extends Array<CmEditor> {

	fields: JSrcFields;
	itemType: ItemType

	ctState: ICtEdState = 'fetching';

	/** état pendant un (ré)init du contenu de l'éditeur. */
	initing = false;

	lockedBy?: ICollabLock | undefined

	constructor(public wspsLive: WspsLive, public wspCd: wspCd, public srcUri: srcUri, public wspUri: wspUri, public wspUriItem: wspUri) {
		super();
	}

	cmContent: Text = Text.empty;

	async refetchContent(): Promise<void> {
		this.cancelSaveTimer();
		this.setCtEdState("fetching");
		try {
			const qs = IO.qs("format", "stream", "param", this.wspCd, "refUri", this.srcUri, "srcTrashed", "false", "fields", this.wspsLive.getSrcFieldNames(this.srcUri));
			const resp = await this.wspsLive.wspServer.config.wspSrcUrl.fetch(qs, 'text');
			if (!resp.ok) {
				if (resp.status === EHttpStatusCode.notFound) {
					resp.asText = "";
				} else {
					this.setCtEdState("fetchFailed");
					console.log("Get Text failed for " + this.wspUri, resp.error);
					return null;
				}
			}
			const fields = resp.headers.get("X-SCFIELDS");
			if (!fields) {
				this.setCtEdState("fetchFailed");
				return null;
			}
			this.fields = JSON.parse(fields) as JSrcFields;
			const wsp = this.wspsLive.findWsp(this.wspCd, true);
			if (!wsp.isAvailable) await wsp.waitForAvailable();
			this.itemType = wsp.wspMetaUi.getItemType(this.fields.itModel);
			const cmText = Text.of(resp.asText.split(/\r\n?|\n/));
			this.initing = true;
			try {
				for (const cmEd of this) {
					// cmEd.editorView.setState(EditorState.create({
					// 	doc: cmText,
					// 	extensions: cmEd.editorView.state.EditorStateConfig.extensions
					// }));
					cmEd.editorView.dispatch({changes: {from: 0, to: cmEd.editorView.state.doc.length, insert: cmText}})
				}
			} finally {
				this.initing = true;
			}
			this.setCtEdState("fresh");
		} catch (e) {
			this.setCtEdState("fetchFailed");
			throw e;
		}
	}


	async save(fromWsp: Wsp, uiContext: HTMLElement | null): Promise<void> {
		if (this.ctState !== "dirty") return;
		this.cancelSaveTimer();
		const qs = IO.qs("cdaction", "PutSrc", "param", this.wspCd, "refUri", this.srcUri, "clId", this.wspsLive.wspServer.chain.clId, "fields", this.wspsLive.getSrcFieldNames(this.srcUri));
		this.setCtEdState("saving");
		const txt = this.cmContent.sliceString(0);
		try {
			const desc = await this.wspsLive.wspServer.config.wspSrcUrl.fetchJson<JSrcFields>(qs, {method: "PUT", body: txt});
			if (!desc) throw Error("saveContent result desc is null");
			if (!fromWsp.isAvailable) await fromWsp.waitForAvailable();
			this.wspsLive._getStates(this.wspUri, false, null).then(this.refreshLockState.bind(this));
			this.itemType = fromWsp.wspMetaUi.getItemType(this.fields.itModel);
			if (ITEM.getSrcUriType(desc.srcUri) === "res") {
				const itemFields = await fromWsp.fetchShortDesc(ITEM.extractItemUri(desc.srcUri));
				await this.wspsLive.dispatchLocalChange(null, fromWsp, itemFields, EWspChangesEvts.u, itemFields);
			} else {
				await this.wspsLive.dispatchLocalChange(null, fromWsp, desc, EWspChangesEvts.u, desc);
			}
			this.setCtEdState("fresh");
		} catch (e) {
			for (const cmEd of this) cmEd.contentEvents.emitCatched("onCtStateChange", "saveFailed", e);
			if (e instanceof RespError && e.response.status === EHttpStatusCode.forbidden) {
				navigator.clipboard.writeText(txt).then(() => {
					POPUP.showNotifForbidden("Vous ne disposez plus des droits en édition, vos modifications annulées ont été copiées dans le presse-papier.", uiContext, {autoHide: 20000});
				}, () => {
					POPUP.showNotifForbidden("Vous ne disposez plus des droits en édition, vos modifications ont dû être annulées.", uiContext, {autoHide: 20000});
				});
				this.refetchContent();
			} else {
				POPUP.showNotifError("Échec à l'enregistrement, connexion au serveur impossible. Attention, vous risquez de perdre vos modifications.", uiContext);
				this.setCtEdState("dirty");
				this.launchSaveTimer();
			}
		}
	}

	canChangeCmDoc() {
		//Que si état stable et pas locked
		return this.initing || (!this.lockedBy && this.ctState === 'dirty' || this.ctState === 'fresh' || this.ctState === 'saving');
	}

	onDocChanged() {
		if (this.ctState === 'fresh') {
			this.launchSaveTimer();
			this.setCtEdState('dirty');
		}
	}

	protected _saveTimer: number;

	protected cancelSaveTimer() {
		if (this._saveTimer) clearInterval(this._saveTimer);
	}

	protected launchSaveTimer() {
		const saveInterval = this.itemType.reg.getPref("item.saveInterval", 10000);
		if (saveInterval) this._saveTimer = setTimeout(() => {
			this._saveTimer = 0;
			this.save(this.wspsLive.findWsp(this.wspCd, true), null);
		}, saveInterval);
	}

	remove(cmEditor: CmEditor) {
		if (this.length === 1) {
			if (this[0] === cmEditor) (this.wspsLive.cmEditors as CmEditors).removeSyncEd(this);
		} else {
			const idx = this.indexOf(cmEditor);
			if (idx >= 0) this.splice(idx, 1);
		}
	}

	setCtEdState(stEvt: ICtEdState, e?: any) {
		if (this.ctState !== stEvt) {
			if (this.ctState === "dirty") {
				this.ctState = stEvt;
				this.wspsLive._wsFrame.postMsg({svc: 'wspWorking', type: 'state', uri: this.wspUri, state: this.wspsLive._getMergedServerStateFor(this.wspUri)} as JWspWorkingMsgStateOut);
			} else {
				this.ctState = stEvt;
				if (stEvt === "dirty") this.wspsLive._wsFrame.postMsg({svc: 'wspWorking', type: 'state', uri: this.wspUri, state: EWspWorkingSt.w} as JWspWorkingMsgStateOut);
			}
			for (const cmEd of this) cmEd.contentEvents.emitCatched("onCtStateChange", stEvt, e);
		}
	}

	refreshLockState(uriStates: JWspWorkingState[]) {
		const writeState = uriStates ? uriStates.find((st) => {return st.state === EWspWorkingSt.w}) : null;
		if (writeState) this.lock(writeState.clId, writeState.account);
		else if (this.lockedBy != null) this.unlock();
	}

	lock(byClId: string, byAccount: string) {
		if (this.ctState === "dirty") {
			//Conflit (race condition) !
			//reload de l'editeur.
			this.refetchContent();
			return;
		}
		if (!this.lockedBy || this.lockedBy.clId !== byClId || this.lockedBy.account !== byAccount) {
			this.lockedBy = {clId: byClId, account: byAccount};
			for (const ed of this) ed.contentEvents.emitCatched("onLock", true);
		}
	}

	unlock() {
		this.lockedBy = null;
		for (const ed of this) ed.contentEvents.emitCatched("onLock", false);
	}
}

class CmEditor implements ICmEditor {

	contentEvents = new EventsMgr<IContentCmEditorEvents>();

	editorView: EditorView

	get wspUri(): wspUri {return this.syncEd.wspUri}

	get contentState(): ICtEdState {return this.syncEd.ctState}

	get lockedBy(): ICollabLock | undefined {return this.syncEd.lockedBy}

	constructor(public reg: IReg<IItemUiEnv>, public syncEd: SynchedEditors) {
	}

	save(): Promise<void> {
		return this.syncEd.save(this.reg.env.wsp, this.editorView.dom);
	}

	close() {
		this.syncEd.remove(this);
		this.editorView.destroy();
	}

	setView(cmView: EditorView) {
		this.editorView = cmView;
	}

	cmDispatch(tr: Transaction) {
		if (tr.docChanged) {
			if (!this.syncEd.canChangeCmDoc()) return; //on bloque tout changement pendant un état instable.
			this.editorView.update([tr]);
			this.syncEd.cmContent = this.editorView.state.doc;
			this.syncEd.forEach((cmEd) => {
				if (cmEd !== this) {
					cmEd.editorView.update([cmEd.editorView.state.update({
						changes: tr.changes,
						annotations: Transaction.remote.of(true)
					})]);
				}
			});
			this.syncEd.onDocChanged();
		} else {
			this.editorView.update([tr]);
		}
	}
}

/** Création d'un nouvel éditeur. */
export function createCmEditor(srcUri: srcUri, reg: IReg<IItemUiEnv>, extensions: Extension, root?: Document | ShadowRoot): ICmEditor {
	const wspsLive = reg.env.wsp.wspServer.wspsLive;
	const cmEditors = wspsLive.cmEditors as CmEditors || (wspsLive.cmEditors = new CmEditors(wspsLive));
	const syncEd = cmEditors.getOrCreateSyncEd(reg.env.wsp.code, srcUri);
	const cmEd = new CmEditor(reg, syncEd);
	syncEd.push(cmEd);
	cmEd.setView(new EditorView({
		state: EditorState.create({doc: syncEd.cmContent, extensions}),
		root: root,
		dispatch: cmEd.cmDispatch.bind(cmEd)
	}));
	return cmEd;
}
